import {
  ChartNumberType,
  ExpressionPostfixOnColumnName,
  IChartSettings,
  IDisplayedSlice,
  ILineMetaNewExpression,
  ILineMetaType,
  IParsedChartDataExpression,
  IParsedChartDataRecord,
  IParsedExpressionColumn,
} from '../interfaces';
import { IChartLine } from './useChartLines';
import { getValueFromDataPoint } from '../../../utils/redux/util';
import { isOnlyOverallSliceKeys } from '../utils';

interface IProps {
  visibleLines: IChartLine[];
  chartData: IParsedChartDataExpression;
  chartSettings: IChartSettings;
  displayedSlices: IDisplayedSlice[];
  chartNumberType: ChartNumberType;
  skipPercentView?: boolean;
}

export enum OtherLineName {
  name = 'Others',
  key = 'others',
}

export enum OtherSlice {
  name = 'Others',
  key = 'others',
}

const calculateOthersValues = (
  dayData: IParsedChartDataRecord,
  visibleMeasureKey: string,
  columns: IParsedExpressionColumn[],
  visibleSliceKeys: string[],
  isComparison: boolean,
): {
  othersValue: number;
  othersPercent: number;
} => {
  let othersValue = getValueFromDataPoint(dayData, visibleMeasureKey) ?? 0;
  let othersPercent =
    getValueFromDataPoint(
      dayData,
      visibleMeasureKey + ExpressionPostfixOnColumnName.percentKey,
    ) ?? 1;

  for (const column of columns) {
    if (
      column.measureKey === visibleMeasureKey &&
      visibleSliceKeys.includes(column.sliceKey) &&
      column.key !== visibleMeasureKey &&
      column.isComparison === isComparison
    ) {
      const columnDayValue = getValueFromDataPoint(dayData, column.key) ?? 0;
      othersValue -= columnDayValue;

      const columnPercentValue =
        getValueFromDataPoint(
          dayData,
          column.key + ExpressionPostfixOnColumnName.percentKey,
        ) ?? 0;
      othersPercent -= columnPercentValue;
    }
  }
  return {
    othersValue,
    othersPercent,
  };
};

export default ({
  chartData,
  visibleLines,
  chartSettings,
  displayedSlices,
  chartNumberType,
  skipPercentView,
}: IProps): [
  IChartLine[],
  ILineMetaNewExpression,
  IParsedChartDataRecord[],
  IDisplayedSlice[],
] => {
  const { columns, linesMeta, data } = chartData;
  const { displayedMeasureSettings } = chartSettings;

  const slicesWithoutComparison = displayedSlices.filter(
    (slice) => !slice.isComparison,
  );

  const filteredVisibleLines = visibleLines.filter((line) =>
    chartNumberType === ChartNumberType.Percentage
      ? linesMeta[line.name]?.type === ILineMetaType.Percent
      : linesMeta[line.name]?.type === ILineMetaType.Base,
  );

  const otherSlice: IDisplayedSlice = {
    key: OtherSlice.key,
    isVisible: true,
    name: OtherSlice.name,
    hint: [],
    originalHint: [],
    userAlias: {
      __typename: 'UserAliasSliceNone',
    },
    hasUserAlias: false,
    rank: null,
    isComparison: false,
    originalSliceKey: OtherSlice.key,
  };
  const displayedSlicesWithOthers = [...slicesWithoutComparison, otherSlice];

  // find the only visible measure.
  const visibleMeasure = displayedMeasureSettings.find(
    (measure) => measure.isVisible,
  );

  // if not visibleMeasure, it means all measures are disabled. return original data.
  if (!visibleMeasure || skipPercentView) {
    return [filteredVisibleLines, linesMeta, data, displayedSlicesWithOthers];
  }

  // find out
  const visibleSlices = slicesWithoutComparison.filter(
    (slice) => slice.isVisible,
  );
  const visibleSliceKeys = visibleSlices.map((slice) => slice.key);
  // if overall and possibly comparison are the only slices displayed, no need to calculate others.
  if (isOnlyOverallSliceKeys(visibleSliceKeys)) {
    return [filteredVisibleLines, linesMeta, data, displayedSlicesWithOthers];
  }

  const othersData = data.map((dayData): IParsedChartDataRecord => {
    const { othersValue, othersPercent } = calculateOthersValues(
      dayData,
      visibleMeasure.key,
      columns,
      visibleSliceKeys,
      false,
    );
    return {
      ...dayData,
      /**
       * It is possible for the others value to become negative because
       * it is not guaranteed that overall = sum(groups)
       * To avoid surprises in the chart and tooltips, clamp the value to 0
       */
      [OtherLineName.key]: Math.max(0, othersValue),
      [OtherLineName.key + ExpressionPostfixOnColumnName.percentKey]: Math.max(
        othersPercent,
        0,
      ),
    };
  });

  // generate visible line for others
  const othersLine: IChartLine = {
    name: OtherLineName.key,
    display: OtherLineName.name,
    color: '#ccc',
    type: 'normal',
    measureKey: visibleMeasure.key,
  };
  const othersPercentLine: IChartLine = {
    name: OtherLineName.key + ExpressionPostfixOnColumnName.percentKey,
    display: OtherLineName.name,
    color: '#ccc',
    type: 'normal',
    measureKey: visibleMeasure.key,
  };
  // generate linesMeta for others
  const othersLineMeta: ILineMetaNewExpression[1] = {
    name: OtherLineName.key,
    display: OtherLineName.name,
    measureKey: visibleMeasure.key,
    percentLineName: OtherLineName.name,
    percentLineKey:
      OtherLineName.key + ExpressionPostfixOnColumnName.percentKey,
    sliceName: OtherSlice.name,
    sliceKey: OtherSlice.key,
    measureName: '',
    measureLabel: undefined,
    type: ILineMetaType.Base,
  };

  const othersPercentLineMeta: ILineMetaNewExpression[1] = {
    name: OtherLineName.key + ExpressionPostfixOnColumnName.percentKey,
    display: OtherLineName.name,
    measureKey: visibleMeasure.key,
    parentKey: OtherLineName.key,
    sliceName: OtherSlice.name,
    sliceKey: OtherSlice.key,
    measureName: '',
    measureLabel: undefined,
    type: ILineMetaType.Percent,
  };

  // if is stacked column chart and Pie Chart, we display value.
  // else if percentage area chart, display percent value.
  const primaryOthersLine =
    chartNumberType === ChartNumberType.Percentage
      ? othersPercentLine
      : othersLine;
  const visibleLinesWithOthers = [...filteredVisibleLines, primaryOthersLine];
  const linesMetaWithOthers = {
    ...linesMeta,
    [OtherLineName.key]: othersLineMeta,
    [OtherLineName.key + ExpressionPostfixOnColumnName.percentKey]:
      othersPercentLineMeta,
  };

  return [
    visibleLinesWithOthers,
    linesMetaWithOthers,
    othersData,
    displayedSlicesWithOthers,
  ];
};
